import { Link } from 'react-router-dom';
import { ArrowDown, ArrowRight, Lock } from 'lucide-react';
import { REACH_DEMO_HREF } from '@/data/reach';
import { trackEvent } from '@/lib/analytics';
import { scrollToId } from '@/lib/scroll';
import { Reveal } from '../Reveal';
import { BrowserFrame, DemoCaption, PhoneFrame } from '../compass/shared';

// Section 1 — premium two-column product hero. Amden Growth green accents
// inside the Amden navy brand frame, matching the Reach product identity.
export const ReachHero = ({ sp }) => (
  <section aria-labelledby="reach-hero-heading" data-testid="reach-hero" className="relative overflow-hidden bg-navy-900 py-20 sm:py-24 lg:py-28">
    <div className="grain-overlay" aria-hidden="true" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8">
      <div>
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold-500" data-testid="reach-eyebrow">
            {sp.heroEyebrow}
          </p>
          {sp.brandLine && (
            <p className="mt-2 text-sm font-extrabold uppercase tracking-[0.3em] text-service-growth" data-testid="reach-brand-line">
              {sp.brandLine}
            </p>
          )}
          <h1 id="reach-hero-heading" className="mt-4 text-balance text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="reach-hero-heading">
            {sp.heroHeading}
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">{sp.heroCopy}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to={REACH_DEMO_HREF}
              data-testid="reach-demo-cta"
              onClick={() => trackEvent('reach_demo_click')}
              className="group inline-flex items-center gap-2 rounded-md bg-gold-500 px-6 py-3.5 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900"
            >
              Book a Reach Demonstration
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
            <button
              type="button"
              data-testid="reach-explore-cta"
              onClick={() => { trackEvent('reach_explore_click'); scrollToId('reach-pipeline'); }}
              className="inline-flex items-center gap-2 rounded-md border-2 border-white/25 px-6 py-3 text-sm font-bold text-white transition-colors hover:border-gold-500 hover:text-gold-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
            >
              Explore How It Works
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {sp.loginUrl && (
            <a
              href={sp.loginUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reach-login-link"
              onClick={() => trackEvent('reach_login_click')}
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-slate-300 underline-offset-4 transition-colors hover:text-gold-500 hover:underline"
            >
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              Existing Client Login
            </a>
          )}
          <ul className="mt-8 flex max-w-xl flex-wrap gap-2" aria-label="Core capabilities">
            {(sp.capabilityLabels || []).map((label) => (
              <li key={label}>
                <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-bold text-slate-200">
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="relative">
        <BrowserFrame shot={sp.shots.dashboard} eager label="Amden Reach — demonstration workspace" data-testid="reach-hero-visual" />
        <div className="absolute -bottom-8 -right-2 hidden w-32 sm:block lg:-right-6 lg:w-36">
          <PhoneFrame shot={sp.shots.plannerMobile} />
        </div>
        <DemoCaption className="text-slate-500 sm:pr-36 sm:text-left" />
      </Reveal>
    </div>
  </section>
);
